<script>
export default {
	name: 'DestinationsListingCard',
	inheritAttrs: false,
};
</script>

<script setup>
import FontAwesomeLazy from '~/components/FontAwesomeLazy.vue';
import { useIsRTL } from '~/logic/helpers/is-rtl.js';

const props = defineProps({
	ctaLabel: { type: String, default: '' },
	title: { type: String, default: '' },
	path: { type: String, default: '' },
	imageUrl: { type: String, default: '' },
	imageAltText: { type: String, default: '' },
	locations: { type: Number, default: 0 },
	locationLabel: { type: String, default: '' },
	locationLabels: { type: String, default: '' },
	destinationTileIconText: { type: String, default: '' },
	destinationTileDescription: { type: String, default: '' },
	iconType: { type: String, default: '' },
	iconId: { type: String, default: '' },
	iconImage: { type: String, default: '' },
	componentIdAA: { type: String, default: '' },
});

const { isRTL } = useIsRTL();
</script>

<template>

<div class="Card hover:shadow-type-a text-primary-black-base hover:no-underline group w-full">
	<AppHyperlink
		v-if="props.imageUrl"
		v-aa="[
			{
				clickName: props.title,
				clickComponentType: 'URL',
				componentName: 'DestinationsListing',
				componentID: props.componentIdAA,
			},
			{
				name: props.imageAltText,
				type: 'other',
			},
		]"
		class="w-full"
		:href="props.path"
	>
		<div
			class="CardImage w-full"
			:style="{
				...(props.imageUrl ? { 'background-image': `url(${props.imageUrl})` } : {}),
			}"
			:title="props.imageAltText"
		>
			<span class="sr-only">{{ props.imageAltText }}</span>
		</div>
	</AppHyperlink>
	<div class="CardContent" :class="{ 'rounded-top': !props.imageUrl }">
		<div class="flex flex-col h-full">
			<div class="flex-grow">
				<h5 class="text-lg font-bold">{{ props.title }}</h5>
				<div
					v-if="props.locations == 1 && props.locationLabel ||
						props.locations > 1 && props.locationLabels ||
						props.destinationTileIconText ||
						props.destinationTileDescription"
					class="mt-4"
				>
					<p
						v-if="props.locationLabel && props.locations == 1"
						class="text-sm"
					>
						{{ props.locations }} {{ props.locationLabel }}
					</p>
					<p
						v-if="props.locationLabels && props.locations > 1"
						class="text-sm"
					>
						{{ props.locations }} {{ props.locationLabels }}
					</p>
					<p
						v-if="props.iconType != 'none'
							&& props.destinationTileIconText"
						class="text-sm mt-4 flex items-center"
					>
						<FontAwesomeLazy
							v-if="props.iconType === 'icon-font-awesome-id'"
							class="fill-secondary-gold-base w-3.5 h-3.5 mr-3 flex items-center"
							:iconId="props.iconId"
						/>
						<img
							v-if="props.iconType === 'icon-image'"
							class="w-4 h-4 mr-3"
							:src="props.iconImage"
						/>
						{{ props.destinationTileIconText }}
					</p>
					<p
						v-if="props.destinationTileDescription"
						v-html-sanitize="props.destinationTileDescription"
						class="author-html-container text-sm mt-4"
					>
					</p>
				</div>
			</div>
			<AppHyperlink
				v-if="props.ctaLabel"
				v-aa="[
					{
						clickName: props.title,
						clickComponentType: 'URL',
						componentName: 'DestinationsListing',
						componentID: props.componentIdAA,
					},
					{
						name: props.ctaLabel,
						type: 'other',
					},
				]"
				:href="props.path"
				class="text-sm mt-6 font-semibold group-hover:(text-primary-blue-light no-underline)"
			>
				{{ props.ctaLabel }}
				<icon-far-arrow-right
					class="cta-icon group-hover:(fill-primary-blue-light translate-x-1 transform rtl:-translate-x-1 rtl:-rotate-180)"
				/>
			</AppHyperlink>
		</div>
	</div>
</div>
</template>



<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.Card {
	width: 100%;
	display: flex;
	flex-direction: column;
	border-radius: 24px;
	transition: .25s ease-out;
}
.cta-icon {
	transition: .25s ease-out;
	flex-shrink: 0;
	fill: var(--primary-blue-base);
	@apply text-sm ml-2.5;

	[dir="rtl"] & {
		@apply ml-0 mr-2.5 transform -rotate-180;
	}
}
.CardImage {
	padding-top: 66.67%;
	// background-image: url('~/assets/batik-pattern-brighter.svg');
	border-radius: 24px;
	border-bottom-right-radius: 0;
	border-bottom-left-radius: 0;
	background-size: cover;
	background-repeat: no-repeat;
}
.CardContent {
	border-radius: 24px;
	flex-grow: 1;
	border-top-right-radius: 0;
	border-top-left-radius: 0;
	border: 1px solid var(--neutral-grey-light);
	@apply p-6 pb-8;

	@media #{var.$query-max-md} {
		padding: 20px 24px;
		@apply py-5 px-6
	}

	&.rounded-top {
		border-radius: 24px;
	}
}

</style>
